import { FaExchangeAlt } from 'react-icons/fa';
import { Handle, Position } from '@xyflow/react';
import { ReturnIdDropdown } from '../../../ReturnIdDropdown/ReturnIdDropdown';
import { CygnusTooltip } from 'reusable-components/CygnusTooltip/CygnusTooltip';
import styles from './K1HierarchyNode.module.css';
import type { K1HierarchyNodeData } from './K1HierarchyNode.types';
import { getNodeBadgeState } from './K1HierarchyNode.utils';

export function K1HierarchyNode({ data }: { data: K1HierarchyNodeData }) {
  const {
    badgeClassName,
    badgeTitle,
    canInteract,
    hasImportData,
    hasReturnId,
    isSelected,
    statusDotClassName,
    statusTooltip,
    statusText,
  } = getNodeBadgeState(data);
  const statusContent = (
    <span className={styles.nodeStatus}>
      <span className={`${styles.nodeDot} ${statusDotClassName}`} />
      {statusText}
    </span>
  );

  return (
    <div
      className={`${styles.node} ${data.isCurrentClient ? styles.nodeCurrentClient : ''} ${isSelected ? styles.nodeSelected : ''}`}
    >
      <Handle type="target" position={Position.Top} className={styles.handle} />

      {canInteract && (
        <CygnusTooltip text={badgeTitle}>
          <div
            className={`${styles.nodeCheckBadge} ${badgeClassName}`}
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              if (hasReturnId && data.docData) {
                data.onToggleSelection?.(String(data.docData.DocumentId));
              }
            }}
          >
            {isSelected ? (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 5L4.2 7.5L8 2.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                <circle cx="4" cy="4" r="3" stroke="currentColor" strokeWidth="1.4" />
              </svg>
            )}
          </div>
        </CygnusTooltip>
      )}

      <div className={styles.nodeTopRow}>
        <span className={styles.nodeBadge}>{data.formType}</span>
        {hasImportData ? (
          statusTooltip ? (
            <CygnusTooltip text={statusTooltip}>
              {statusContent}
            </CygnusTooltip>
          ) : statusContent
        ) : null}
      </div>

      <p className={styles.nodeName}>{data.partnership}</p>

      {data.taxId && (
        <p className={styles.nodeId}>ID: {data.taxId}</p>
      )}

      {canInteract && data.docData && (
        <div className={styles.nodeDropdown}>
          <ReturnIdDropdown
            value={data.selectedReturnId || ''}
            clientMasterId={data.docData.ClientMasterId}
            parentClientMasterId={data.docData.ParentClientMasterId}
            onChange={(option) => data.onSelectReturnId?.(data.docData, option)}
          />
        </div>
      )}

      {canInteract && (
        <button
          className={`${styles.nodeMapActivities} ${!hasReturnId ? styles.nodeMapActivitiesDisabled : ''}`}
          disabled={!hasReturnId}
          title={!hasReturnId ? 'Select a Return ID to enable Map Activities' : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (hasReturnId && data.docData) {
              data.onMapActivities?.(
                data.selectedReturnId,
                data.docData.SourceTaxId,
                data.docData.DocumentId,
                data.docData.SourceName,
              );
            }
          }}
        >
          <FaExchangeAlt />
          Map Activities
        </button>
      )}

      <Handle type="source" position={Position.Bottom} className={styles.handle} />
    </div>
  );
}
